(() => {
  const LEGACY_ROOT_HANDLES = [
    'custom-ecu-tuning-home',
    'performance-parts',
    'off-road-4x4-accessories',
    'camping-equipment',
    'merchandise',
  ];

  const ROOT_LABELS = {
    'custom-ecu-tuning-home': 'Custom ECU Tuning',
    'performance-parts': 'Performance Parts',
    'off-road-4x4-accessories': 'Vehicle Accessories',
    'camping-equipment': 'Camping & Expedition',
    merchandise: 'Merch',
  };

  const FALLBACK_CHILDREN = {
    merchandise: [
      'hats-caps',
      'hoodies-jumpers',
      'merch-accessories',
      't-shirts-vests',
    ],
  };

  // Product types come from the accepted tag contract. These navigation branches
  // contain vehicle makes, which must never be interpreted as type:<make-hub>.
  const PRODUCT_TYPE_REFINEMENTS = {
    exhausts: [{ handle: 'exhaust-system', label: 'Exhaust System', taxonomy: { type: 'exhaust-system' } }],
    'vehicle-tuning': [{ handle: 'ecu-remap', label: 'ECU Remap', taxonomy: { type: 'ecu-remap' } }],
  };

  const normaliseHandle = (value) => (value || '').toLowerCase().trim();
  const nodeHandleFromId = (value) => normaliseHandle(value).replace(/^collection:/, '');
  const displayLabel = (node) => node?.optionLabel || node?.label || ROOT_LABELS[node?.handle] || node?.handle || '';
  const isAvailableNode = (node) => Boolean(
    node
    && node.handle
    && node.visibility?.routeValid !== false
    && Number(node.counts?.effectiveProducts || node.counts?.directProducts || 0) > 0
  );

  const initTaxonomyFilter = (filter, manifest) => {
    if (filter.dataset.taxonomyReady === 'true') return;

    const ROOT_HANDLES = manifest.browse?.roots || LEGACY_ROOT_HANDLES;
    const nodes = { ...manifest?.nodes };
    const refinements = manifest.browse?.filterOnlyRefinements || PRODUCT_TYPE_REFINEMENTS;
    Object.values(refinements).flat().forEach((node) => { nodes[node.handle] = node; });
    const areaSelect = filter.querySelector('[data-taxonomy-level="area"]');
    const categorySelect = filter.querySelector('[data-taxonomy-level="category"]');
    const typeSelect = filter.querySelector('[data-taxonomy-level="type"]');
    const pathStatus = filter.querySelector('[data-taxonomy-path]');
    const currentHandle = normaliseHandle(filter.dataset.currentHandle);
    const currentNode = nodes[currentHandle];
    const ancestorHandles = (currentNode?.hierarchy?.ancestorIds || []).map(nodeHandleFromId);
    const currentPathHandles = [...ancestorHandles, currentHandle].filter(Boolean);
    const currentPath = new Set(currentPathHandles);

    const allRootNodes = ROOT_HANDLES
      .map((handle) => nodes[handle])
      .filter(isAvailableNode);

    const childNodes = (node) => {
      const childIds = node?.browse?.owned
        ? node.hierarchy.childIds
        : node?.hierarchy?.childIds?.length
          ? node.hierarchy.childIds
          : (FALLBACK_CHILDREN[node?.handle] || []).map((handle) => `collection:${handle}`);

      return childIds
        .map(nodeHandleFromId)
        .map((handle) => nodes[handle])
        .filter(isAvailableNode)
        .sort((left, right) => {
          const orderDifference = Number(left.hierarchy?.order || 1000) - Number(right.hierarchy?.order || 1000);
          return orderDifference || (left.label || left.handle).localeCompare(right.label || right.handle);
        });
    };

    const pathNode = (options) => options.find((node) => currentPath.has(node.handle));
    const currentArea = pathNode(allRootNodes);
    const currentCategories = childNodes(currentArea);
    const currentCategory = pathNode(currentCategories);
    const typeNodes = (category) => {
      if (refinements[category?.handle]) return refinements[category.handle];
      if (!category?.browse?.owned) return childNodes(category);
      if (category.browse.kind === 'service') return [];
      const choices = [], seen = new Set();
      const visit = (parent, labels = []) => childNodes(parent).forEach(child => {
        if (seen.has(child.handle) || child.browse?.kind !== 'product') return;
        seen.add(child.handle);
        const branch = [...labels, child.label];
        choices.push({ ...child, optionLabel: branch.join(' / ') });
        visit(child, branch);
      });
      visit(category);
      return choices;
    };
    const currentTypes = typeNodes(currentCategory);
    const currentType = [...currentTypes].reverse().find(node => currentPath.has(node.handle));
    const isGovernedScope = Boolean(currentArea);
    const rootOptions = isGovernedScope ? [currentArea] : allRootNodes;
    const defaultSelection = {
      area: currentArea?.handle || '',
      category: currentCategory?.handle || '',
      type: currentType?.handle || '',
    };
    const urlParams = new URLSearchParams(window.location.search);
    const requestedSelection = {
      area: normaliseHandle(urlParams.get('product_area')),
      category: normaliseHandle(urlParams.get('product_category') || urlParams.get('brand_category')),
      type: normaliseHandle(urlParams.get('product_type')) === 'exhaust-system' && manifest.browse ? 'exhaust-systems' : normaliseHandle(urlParams.get('product_type')),
    };

    // Retired product-type bookmarks follow only an explicit canonical replacement.
    // Keep the current brand, vehicle and other query refinements intact.
    const retiredType = nodes[requestedSelection.type];
    const retiredTypeReplacement = nodes[retiredType?.browse?.legacy?.replacement];
    if (retiredType?.visibility?.retired && retiredType?.browse?.owned && retiredType.semanticType === 'type'
        && retiredTypeReplacement?.browse?.owned && retiredTypeReplacement.browse.kind === 'product'
        && retiredTypeReplacement.semanticType === 'type' && retiredTypeReplacement.visibility?.published
        && !retiredTypeReplacement.visibility?.retired && retiredType.taxonomy?.cat
        && retiredType.taxonomy?.cat === retiredTypeReplacement.taxonomy?.cat
        && isAvailableNode(retiredTypeReplacement)) {
      const destination = new URL(window.location.href);
      destination.searchParams.set('product_type', retiredTypeReplacement.handle);
      if (destination.searchParams.get('collection_context') === retiredType.handle) destination.searchParams.set('collection_context', retiredTypeReplacement.handle);
      ['product_type_tag', 'product_type_title'].forEach(key => destination.searchParams.delete(key));
      window.location.replace(destination.pathname + destination.search + destination.hash);
      return;
    }

    // Old bookmarks used a genuine vehicle collection as a bogus product type.
    // Follow that existing collection route, keeping every vehicle and other
    // refinement. Its membership scope prevents a Ford bookmark widening to BMW.
    const legacyCategory = nodes[requestedSelection.category];
    const legacyType = nodes[requestedSelection.type];
    if (PRODUCT_TYPE_REFINEMENTS[legacyCategory?.handle]
        && childNodes(legacyCategory).some((node) => node.handle === legacyType?.handle)
        && !legacyType?.taxonomy?.type) {
      const destination = new URL(window.location.href);
      destination.pathname = '/collections/' + legacyType.handle;
      ['product_type', 'product_type_tag', 'product_type_title', 'collection_context'].forEach((key) => destination.searchParams.delete(key));
      window.location.replace(destination.pathname + destination.search + destination.hash);
      return;
    }

    const addOptions = (select, options, placeholder, selectedHandle = '', lockSingle = false) => {
      select.replaceChildren();
      const placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.textContent = placeholder;
      select.appendChild(placeholderOption);

      options.forEach((node) => {
        const option = document.createElement('option');
        option.value = node.handle;
        option.textContent = displayLabel(node);
        option.selected = node.handle === selectedHandle;
        select.appendChild(option);
      });

      const isLocked = options.length === 0 || (lockSingle && options.length === 1);
      select.disabled = isLocked;
      select.dataset.taxonomyLocked = isLocked ? 'true' : 'false';
    };

    const selectedNode = (select) => nodes[normaliseHandle(select?.value)];
    const selectedLabel = (select) => select?.selectedOptions?.[0]?.textContent?.trim() || '';
    const isRefinement = () => (
      areaSelect.value !== defaultSelection.area
      || categorySelect.value !== defaultSelection.category
      || typeSelect.value !== defaultSelection.type
    );

    const publishSelection = (reason = 'change') => {
      const area = selectedNode(areaSelect);
      const category = selectedNode(categorySelect);
      const type = selectedNode(typeSelect);
      const labels = [
        area ? selectedLabel(areaSelect) : '',
        category ? selectedLabel(categorySelect) : '',
        type ? selectedLabel(typeSelect) : '',
      ].filter(Boolean);
      const detail = {
        area: area?.handle || '',
        category: category?.handle || '',
        type: type?.handle || '',
        areaTag: area?.taxonomy?.dept || '',
        categoryTag: category?.taxonomy?.cat || '',
        typeTag: type?.taxonomy?.type || '',
        areaLabel: area ? selectedLabel(areaSelect) : '',
        categoryLabel: category ? selectedLabel(categorySelect) : '',
        typeLabel: type ? selectedLabel(typeSelect) : '',
        isRefinement: isRefinement(),
        reason,
      };

      filter.dataset.selectedArea = detail.area;
      filter.dataset.selectedCategory = detail.category;
      filter.dataset.selectedType = detail.type;
      filter.dataset.selectedAreaTag = detail.areaTag;
      filter.dataset.selectedCategoryTag = detail.categoryTag;
      filter.dataset.selectedTypeTag = detail.typeTag;
      filter.dataset.selectedAreaLabel = detail.areaLabel;
      filter.dataset.selectedCategoryLabel = detail.categoryLabel;
      filter.dataset.selectedTypeLabel = detail.typeLabel;
      filter.dataset.taxonomyRefinement = detail.isRefinement ? 'true' : 'false';
      pathStatus.textContent = labels.length
        ? `Current range: ${labels.join(' / ')}`
        : 'Showing all products in this range.';
      filter.dispatchEvent(new CustomEvent('hoonies:taxonomy-filter-change', { bubbles: true, detail }));
    };

    const renderTypes = (selectedHandle = '') => {
      const category = selectedNode(categorySelect);
      let options = typeNodes(category);
      const lockToCurrent = Boolean(currentType && category?.handle === currentCategory?.handle);
      if (lockToCurrent) options = [currentType];
      addOptions(
        typeSelect,
        options,
        options.length ? 'All product types' : 'No further product types',
        selectedHandle,
        lockToCurrent
      );
      publishSelection();
    };

    const renderCategories = (selectedCategoryHandle = '', selectedTypeHandle = '') => {
      const area = selectedNode(areaSelect);
      let options = childNodes(area);
      const lockToCurrent = Boolean(currentCategory && area?.handle === currentArea?.handle);
      if (lockToCurrent) options = [currentCategory];
      addOptions(
        categorySelect,
        options,
        options.length ? 'All product categories' : 'No product categories',
        selectedCategoryHandle,
        lockToCurrent
      );
      renderTypes(selectedTypeHandle);
    };

    const initialArea = rootOptions.some((node) => node.handle === requestedSelection.area)
      ? requestedSelection.area
      : defaultSelection.area;
    addOptions(areaSelect, rootOptions, 'All product areas', initialArea, isGovernedScope);
    renderCategories(
      requestedSelection.category || defaultSelection.category,
      requestedSelection.type || defaultSelection.type
    );

    areaSelect.addEventListener('change', () => renderCategories());
    categorySelect.addEventListener('change', () => renderTypes());
    typeSelect.addEventListener('change', () => publishSelection());
    filter.addEventListener('hoonies:catalogue-filters-reset', () => {
      addOptions(areaSelect, rootOptions, 'All product areas', defaultSelection.area, isGovernedScope);
      renderCategories(defaultSelection.category, defaultSelection.type);
      publishSelection('reset');
    });
    filter.dataset.taxonomyReady = 'true';
    publishSelection('init');
  };

  let navigationPromise;
  const loadNavigationForFilter = (filter) => {
    if (window.hooniesCollectionNav?.nodes) return Promise.resolve();
    if (navigationPromise) return navigationPromise;
    const url = filter.dataset.navManifestUrl;
    if (!url) return Promise.reject(Error('TAXONOMY_MANIFEST_URL_REQUIRED'));
    navigationPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url; script.dataset.taxonomyNavigation = 'true';
      script.onload = () => {
        if (!window.hooniesCollectionNav?.nodes) { script.remove(); reject(Error('TAXONOMY_MANIFEST_INVALID')); return; }
        if (window.hooniesNav2) window.hooniesNav2.refresh();
        resolve();
      };
      script.onerror = () => { script.remove(); reject(Error('TAXONOMY_MANIFEST_LOAD')); };
      document.head.appendChild(script);
    }).catch(error => { navigationPromise = null; throw error; });
    return navigationPromise;
  };

  const initialise = (root = document, attempt = 0) => {
    const filters = root.querySelectorAll('[data-collection-taxonomy-filter]');
    if (!filters.length) return;

    const manifest = window.hooniesCollectionNav;
    if (!manifest?.nodes) {
      const request = () => loadNavigationForFilter(filters[0]).then(() => initialise(root)).catch(() => {
        filters.forEach(filter => { filter.dataset.taxonomyLoadState = 'error'; });
      });
      filters.forEach(filter => {
        if (filter.dataset.taxonomyDemandBound === 'true') return;
        filter.dataset.taxonomyDemandBound = 'true';
        ['pointerdown', 'focusin'].forEach(type => filter.addEventListener(type, request, {passive:true}));
      });
      const params = new URLSearchParams(window.location.search);
      if (['product_area', 'product_category', 'brand_category', 'product_type'].some(key => params.get(key))) request();
      return;
    }

    filters.forEach((filter) => initTaxonomyFilter(filter, manifest));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
